"use client";

import * as React from "react";
import { ShieldCheck, Wallet, Scale, Gift, LockKeyhole, HelpCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const termsData = [{
  id: 1,
  iconName: "ShieldCheck",
  title: "01. SYSTEM ACCESS & AUTHENTICATION",
  description: "Users must utilize secure authentication protocols. Access is granted based on verified credentials and compliance with regional security standards. Unauthorized attempts to bypass security layers are strictly logged and mitigated via automated threat detection systems."
}, {
  id: 2,
  iconName: "Wallet",
  title: "02. DATA PROCESSING & LATENCY",
  description: "Data ingestion and processing occur within defined latency parameters. We utilize distributed nodes to ensure redundancy. Service Level Agreements (SLAs) define the minimum uptime and throughput expectations for all API endpoints and data pipelines."
}, {
  id: 3,
  iconName: "Scale",
  title: "03. INTEGRITY & PROTOCOL COMPLIANCE",
  description: "All transactions must adhere to established network protocols. Automated validation checks prevent malformed requests. The use of unauthorized scripts, bots, or stress-testing without prior written consent is strictly prohibited and monitored."
}, {
  id: 4,
  iconName: "Gift",
  title: "04. RESOURCE ALLOCATION & SCALING",
  description: "Resources are allocated dynamically based on demand. Fair usage policies prevent single-tenant saturation. We reserve the right to throttle connections or re-prioritize traffic that threatens the overall stability of the shared infrastructure."
}, {
  id: 5,
  iconName: "LockKeyhole",
  title: "05. ENCRYPTION & PRIVACY STANDARDS",
  description: "End-to-end encryption is enforced for all data in transit and at rest. We comply with ISO/IEC 27001 standards. Personal identifiers are obfuscated or deleted according to our automated data retention lifecycle and cryptographic shredding policies."
}];
const iconMap: Record<string, React.ElementType> = {
  ShieldCheck,
  Wallet,
  Scale,
  Gift,
  LockKeyhole
};
function TermCard({
  term,
  index,
  isLast
}: {
  term: typeof termsData[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = iconMap[term.iconName];
  return <div data-section-id="178967" ref={ref} data-index={index} className={cn("transition-all duration-700 ease-out flex flex-col items-center w-full", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      <Card className="w-full relative overflow-hidden bg-card/50 backdrop-blur-md border-l-2 border-l-primary/50 border-t-white/5 border-r-white/5 border-b-white/5 rounded-xl transition-all duration-300 hover:bg-card/70 shadow-xl group">
        {}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        
        <CardHeader className="pb-4 flex flex-row items-center gap-4">
          <div className="bg-primary/10 p-3 rounded-lg border border-primary/20">
            <Icon className="size-8 text-primary" />
          </div>
          <CardTitle className="font-sans text-[32px] font-normal uppercase text-foreground tracking-wider">
            <span className="font-sans opacity-70 mr-2">{term.title.split(' ')[0]}</span>
            {term.title.split(' ').slice(1).join(' ')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-sans text-[16px] text-foreground leading-[1.6]">
            {term.description}
          </p>
        </CardContent>
      </Card>

      {!isLast && <div className="py-10 w-full flex justify-center">
          <Separator className="w-32 h-[1px] bg-primary/20 rounded-full" />
        </div>}
    </div>;
}
export default function DetailedTerms() {
  return <section id="terms-details" className="bg-background text-foreground py-24 px-6 md:px-12 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-[500px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="max-w-4xl mx-auto relative z-10 flex flex-col items-center">
        <div className="text-center mb-16">
          <h2 className="font-sans text-4xl md:text-5xl font-normal uppercase text-foreground tracking-tighter mb-6">
            System Governance & Protocol
          </h2>
          <p className="font-sans text-lg text-foreground max-w-2xl mx-auto">
            Our operational framework ensures high-availability, security, and integrity across all distributed systems and user interactions.
          </p>
        </div>

        <div className="w-full flex flex-col">
          {termsData.map((term, i) => <TermCard key={term.id} term={term} index={i} isLast={i === termsData.length - 1} />)}
        </div>

        <div className="mt-20 text-center flex flex-col items-center gap-6">
          <p className="font-sans text-foreground">
            Require technical clarification regarding our protocols?
          </p>
          <Button asChild size="lg" className="rounded-md px-8 py-6 text-lg font-sans uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg transition-all duration-300 hover:scale-105">
            <Link to="/SupportPage">
              <HelpCircle className="mr-2 size-5" />
              Contact Support
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}